import { TaskEvent } from './task-event';
import { Cat, PriorityLevel } from './task-template';
import { z } from 'zod';

export interface ITaskMessage {
  tenantId: string;
  externalReferenceId: string;
  uniqueIdentifier: string;
  taskType: string;
  templateId: string;
  appointmentDurationInMinutes: number;
  location: {
    streetLine1: string;
    streetLine2: string;
    city: string;
    state: string;
    zip: string;
  };
  claimNumber: string;
  insuredName: {
    firstName: string;
    lastName: string;
  };
  contactInformation: {
    phone: string;
    email: string;
  };
  catCode: string;
  preferredContact: string;
  taskReceived: string;
  sender: string;
  resCom: string;
  role?: string;
  appointment?: string;
  severity?: string;
  altLocation?: {
    streetLine1?: string;
    streetLine2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  phone2?: string;
  altContactInformation?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  preferredLanguage?: string;
  preferredAttributes?: {
    hasLadder?: boolean;
    stateLicense?: boolean;
  };
  peril?: string;
  overridePriority?: string;
  jobNumber?: string;
  mustStartBefore?: string;
  mustStartAfter?: string;
  mustEndBefore?: string;
  note?: string;
  targetQueue?: TargetQueue;
  completed?: string;
  supplementalData?: SupplementalData[];
  id?: string;
}

export const NAQ_ROOT_CAUSES = {
  TimingGap: 'timing_gap',
  PerilMismatch: 'peril_mismatch',
} as const;

export type NaqRootCause = typeof NAQ_ROOT_CAUSES[keyof typeof NAQ_ROOT_CAUSES];

export interface NaqDiagnosisResult {
  rootCauses?: string[];
  timingGapMinutes?: number;
  originalPeril?: string;
  fnolPeril?: string;
  diagnosedAt?: string;
}

export interface NaqRetryResponse {
  success: boolean | null;
  failureReason: string | null;
  completedAt: string | null;
}

/**
 * Determines whether a task sits on the "unassigned" NAQ self-service surface
 * that exposes the retry button and NAQ reason badges.
 *
 * Matches the backend `GetUnassignedTasksCountAsync` rule: a task qualifies when
 * it is in the needs-attention queue, or it is in any queue other than cancelled
 * with no resource assigned.
 */
export function isUnassignedNaqSurface(
  targetQueue: string | undefined,
  assignedResourceId: string | null | undefined
): boolean {
  if (targetQueue === 'needs_attention_q') {
    return true;
  }
  if (targetQueue === 'cancelled_q') {
    return false;
  }
  return !assignedResourceId;
}

/**
 * Opt-in timeline-derived fields on task list and details responses, as the
 * `include` tokens DispatchService accepts: `manualUnassignment` requests
 * `wasManuallyUnassigned`, `lockedEventSent` requests `lockedEventSent`.
 */
export type TaskInclude = 'manualUnassignment' | 'lockedEventSent';

/**
 * Tenants that see the Locked Event Sent column. Must match the tenants for
 * which DispatchService reads `lockedEventSent`.
 */
export const LOCKED_EVENT_SENT_TENANT_IDS: readonly string[] = [
  'allstate',
  'e2e-allstate',
  'loadtest-allstate',
];

/** Whether the tenant sees the Locked Event Sent column (case-insensitive). */
export function isLockedEventSentTenant(tenantId?: string | null): boolean {
  const normalized = tenantId?.toLowerCase();
  return !!normalized && LOCKED_EVENT_SENT_TENANT_IDS.includes(normalized);
}

/**
 * Whether a task list request should opt into `wasManuallyUnassigned`: only the
 * Unassigned view (quick filter) and the Needs Attention queue (Assignment
 * Status filter) show the manual-unassignment designator.
 *
 * Note: `useGetTaskInfos` overwrites `queueName` (adding `needs_attention_q`)
 * when `hideCancelledTasks` is set, but only the Unassigned view sets that, so
 * the result is the same either way.
 */
export function shouldRequestManualUnassignment(
  filters: { status?: string; queueName?: string[] | null } | undefined
): boolean {
  return (
    filters?.status === 'unassigned' ||
    !!filters?.queueName?.includes('needs_attention_q')
  );
}

export interface ITask {
  $msg?: ITaskMessage;
}

export class Task implements ITask {
  static insuredFullName(msg?: ITaskMessage): string {
    return msg
      ? `${msg.insuredName.firstName} ${msg.insuredName.lastName}`
      : '';
  }
}
export interface MessageStatusEntry {
  timestamp: string;
}

export interface CancelledMessageStatusEntry {
  timestamp: string;
  reason: CancelReasons | undefined;
}

export type CancelReasons =
  | 'coverageDenied'
  | 'alternateMoiUsed'
  | 'claimWithdrawal'
  | 'customerNotAvailable'
  | 'weather';

export interface MessageStatus {
  started?: MessageStatusEntry;
  completed?: MessageStatusEntry;
  cancelled?: CancelledMessageStatusEntry;
}

export interface TaskSender {
  tenantId: string;
}

export interface TaskContact {
  primary: ContactInfo;
  secondary: ContactInfo;
  altLocation: TaskLocation;
}
export interface ContactInfo {
  name: string;
  phone?: string;
  phone2?: string;
  email?: string;
  contactMethod?: string;
}

export interface TaskLocation {
  streetLine1?: string;
  streetLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lon?: number;
  timeZoneId?: string;
}
export interface BlobAttribute {
  id: string;
  cat: string;
  name?: string;
  attribute?: string;
  value?: string;
  nonCat: string;
  useProficiency: boolean;
  minimumProficiency: number;
  isPrepopulated: boolean;
}

export interface BlobAssignedResource {
  firstName: string;
  lastName: string;
  id: string;
}

export interface DispatchInformation {
  resourceId: string;
}

export interface TaskAssignmentRequest {
  data: TaskRescheduleRequest;
  taskId: string;
}
export interface TaskSummaryResponse {
  data: TaskSummary[];
  continuationToken: string;
}

export type TaskRescheduleRequest = {
  id?: string;
  resourceId: string;
  reasonCode: string;
  appointmentStart: string;
  appointmentEnd: string;
  isPrecise?: boolean;
};

export type TaskListParams = {
  showMap: boolean;
  view: 'list' | 'scheduler';
};

export const defaultView: TaskListParams = {
  showMap: false,
  view: 'list',
};

export type TaskStatus = 'notstarted' | 'pending' | 'completed' | 'cancelled';

export type TaskLockStatus = 'locked' | 'unlocked';

export interface TaskCancelRequest {
  cancelReason?: string | undefined;
}

export type TaskStatusRequest = {
  reason?: string;
};

export type TaskResponse = TaskSummary & {
  resource?: TaskAssignedResource;
};

export interface RelatedTask {
  id: string;
  claimNumber: string;
  appointment: string;
  taskReceived: string;
  assignedResourceId: string;
  targetQueue: TargetQueue;
  methodOfInspection: string;
  taskType?: {
    type: string;
    friendlyName: string;
  };
}

export interface SupplementalData {
  key: string;
  label: string;
  type: SupplementalDataTypes;
  value: string;
}

export enum SupplementalDataTypes {
  SINGLE_LINE_TEXT = 'single_line_text',
  MULTI_LINE_TEXT = 'multi_line_text',
}

export type TaskGeoTrackingEvents = {
  eventType: string;
  lat: number;
  lon: number;
  timestamp: string;
};

export interface TaskGeoTracking {
  events: TaskGeoTrackingEvents[];
}
export interface TaskSummary {
  id: string;
  tenantId: string;
  $msg: MSG;
  $metadata: MetaData;
  /** Present only when the request opted in; true means the latest adjuster removal-or-assignment timeline entry is a removal not made by the optimizer. */
  wasManuallyUnassigned?: boolean;
  // the location of these will change. Waiting on api update.
  lossLat?: number;
  lossLong?: number;
  $geoTracking?: TaskGeoTracking;
  $relatedTasks?: RelatedTask[];
  requiredAttributes?: {
    [attributeDefinitionIntegrationGUID: string]: string[];
  };
}

export type TargetQueue =
  | 'input_q'
  | 'auto_dispatch_q'
  | 'manual_dispatch_q'
  | 'dispatched_q'
  | 'needs_attention_q'
  | 'wip_q'
  | 'egress'
  | 'cancelled_q';
interface MetaData {
  targetQueue: TargetQueue;
  appliedEfficiencyCoefficient: number;
  naqDiagnosis?: NaqDiagnosisResult;
}

export enum PropertyTypeEnum {
  RESIDENTIAL = 'residential',
  COMMERCIAL = 'commercial',
}

export const PropertyTypeOptions = [
  { title: 'Residential', value: PropertyTypeEnum.RESIDENTIAL },
  { title: 'Commercial', value: PropertyTypeEnum.COMMERCIAL },
];

export interface TaskForm extends Partial<TaskSummary> {
  claim: {
    lossLocation?: {
      streetLine1?: string;
      streetLine2?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
    jobId?: string;
    taskId?: string;
    claimNumber?: string;
    insuredName?: string;
    catCode?: string;
    propertyType?: string;
    dateOfLoss?: string;
    dateOfNotice?: string;
    peril?: string;
    sourcedFrom?: string;
    isModularHome?: boolean;
    jobNumber?: string;
    locations?: { locationType: string; location: string }[];
    timeZone?: string;
  };
  contact: {
    primary: {
      lastName?: string;
      firstName?: string;
      phone?: string;
      email?: string;
      contactMethod?: string;
      phone2?: string;
      insuredName?: string;
      notes?: string;
    };
    secondary: {
      lastName?: string;
      firstName?: string;
      phone?: string;
      email?: string;
      contactMethod?: string;
      phone2?: string;
      insuredName?: string;
      notes?: string;
    };
    altLocation?: {
      streetLine1?: string;
      streetLine2?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
  };
  assignmentHandling: {
    travel?: boolean;
    isStateLicenseRequired?: boolean;
    templateId?: string;
    durationInMinutes?: number;
    priority?: string;
    appointment?: string;
    severity?: string;
    methodOfInspection?: string;
    overridePriority?: boolean;
    notes?: string;
    preferredLanguage?: string;
    appointmentStart?: string;
    appointmentEnd?: string;
  };
  attributes: any[]; // waiting for the schema for this. It differs per task
  needsReason: boolean;
  reasonCode: string;
}

export interface TaskClaim {
  lossLocation?: TaskLocation;
  jobId?: string;
  taskId: string;
  claimNumber: string;
  insuredName: string;
  catCode: string;
  propertyType: PropertyTypeEnum;
  dateOfLoss: string;
  dateOfNotice?: string;
  peril: string;
  sourcedFrom: string;
  isModularHome: boolean;
  jobNumber: string;
  supplementalData?: SupplementalData[];
}

export interface AssignmentHandling {
  travel: boolean;
  templateId: string;
  durationInMinutes: number;
  priority: PriorityLevel;
  appointment: string;
  severity: string;
  methodOfInspection: string;
  overridePriority: boolean;
  mustStartBefore: string;
  mustStartAfter: string;
  mustEndBefore: string;
  notes: string;
  preferredLanguage: string;
  taskGroup: string;
  targetQueue: TargetQueue;
  assignmentCategory: string;
  lockOnIngress: boolean;
}

export interface TaskAssignedResource {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  teamId: string;
  officeId: string;
}

export interface TaskAttributes {
  id: string;
  cat: Cat;
  nonCat: Cat;
  useProficiency: boolean;
  minimumProficiency: number;
  isPrepopulated: boolean;
}
export interface MSG {
  externalId: string;
  sender: {
    tenantId: string;
  };
  claim: TaskClaim;
  contact: TaskContact;
  assignmentHandling: AssignmentHandling;
  requiredResource?: TaskAssignedResource;
  assignedResource: TaskAssignedResource;
  attributes: TaskAttributes[];
  status?: MessageStatus;
  taskReceived: string;
  lockedAt?: string;
  taskType?: string;
}

export type TaskInfo = {
  appointment: string;
  appointmentDurationInMinutes: number;
  assignedResource: string;
  assignedResourceId: string;
  assignedResourceTeamId: string;
  cId?: null;
  catCode: string;
  claimNumber: string;
  completed?: null;
  dataUrl: string;
  dateOfLoss: string;
  dateOfNotice: string;
  driveStartTime: string;
  extRefId: string;
  id: string;
  insuredName: string;
  jobNumber?: null;
  lockedAt?: null;
  lossCity: string;
  lossCounty?: null;
  lossRegion: string;
  lossState: string;
  lossZip: string;
  lossLong?: number;
  lossLat?: number;
  /** Office of the assigned resource. Absent when unassigned or unset. */
  officeName?: string;
  peril: string;
  priority: string;
  requiredResource?: string | null;
  requiredResourceId?: string | null;
  sender: string;
  severity: string;
  src?: null;
  started?: null;
  targetQueue: string;
  taskReceived: string;
  templateId: string;
  tenantId: string;
  methodOfInspection?: string;
  taskType: string;
  $metadata?: {
    targetQueue?: string;
    naqDiagnosis?: NaqDiagnosisResult;
  };
  naqFailureMessage?: string;
  /** Present only when the request opted in; true means the latest adjuster removal-or-assignment timeline entry is a removal not made by the optimizer. */
  wasManuallyUnassigned?: boolean;
  /** Present only when the request opted in (Allstate only); true means an assignment-locked event was sent for the task. Absent when not requested or the lookup failed. */
  lockedEventSent?: boolean;
};

export interface ReassignFormBody {
  resourceId: string;
  reasonCode: string;
  assignmentHandling: {
    appointmentStart: string;
    appointmentEnd: string;
  };
  claim: { timeZone: string };
}

export enum TaskType {
  COMPLEX = 'COMPLEX',
  COMPLEX_CHILD = 'COMPLEX_CHILD',
  NON_COMPLEX = 'NON_COMPLEX',
  CLAIM_OWNER = 'CLAIM_OWNER',
  CLAIM_HANDLER = 'CLAIM_HANDLER',
}

export const getTaskType = (taskType?: string): TaskType => {
  switch (taskType) {
    case 'COMPLEX':
      return TaskType.COMPLEX;
    case 'COMPLEX_CHILD':
      return TaskType.COMPLEX_CHILD;
    case 'CLAIM_OWNER':
      return TaskType.CLAIM_OWNER;
    case 'CLAIM_HANDLER':
      return TaskType.CLAIM_HANDLER;
    case 'NON_COMPLEX':
      return TaskType.NON_COMPLEX;
    default:
      return TaskType.NON_COMPLEX;
  }
};

export type TaskAssignmentOrEvent = DispatchTaskAssignmentView | TaskEvent;

export interface DispatchTaskAssignmentView {
  id?: string;
  assignmentId?: string;
  assignedResourceId?: AssignedResourceId;
  assignedResource?: string;
  assignmentAction?: AssignmentAction;
  initiator?: Initiator;
  tenantId?: string;
}

export interface Initiator {
  type?: string;
  id?: string;
  name?: string;
  email?: string;
}

export interface AssignedResourceId {
  current?: string;
  previous?: string;
}

export class Reason {
  code: string | undefined;
  detail: string | undefined;
}

export enum AssignmentActionType {
  FORCED,
  UI_REASSIGN,
  OPTIMIZED,
  INITIAL,
}
export interface AssignmentAction {
  actionType: AssignmentActionType;
  reason: Reason;
  dateTime: Date;
}

export const LossAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullable(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  timezone: z.string(),
});

export const ClaimContactSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  primaryPhone: z.string().nullable(),
  alternatePhone: z.string().nullable(),
  email: z.string().nullable(),
});

export const ClaimSchema = z.object({
  uniqueId: z.string(),
  claimNumber: z.string(),
  peril: z.string(),
  catastropheNumber: z.string().nullable(),
  propertyTypes: z.array(z.string()),
  lossAddress: LossAddressSchema,
  claimContact: ClaimContactSchema,
  lossDate: z.string(),
  creationDate: z.string(),
});

export type UIValidationRulesResponse = {
  padClaimNumber: boolean;
};

export interface CollapseNonWorkingTimeConfig {
  fromDay: number;
  toDay: number;
  fromHour: number;
  toHour: number;
}

// Shape of `configuration/{tenantId}/bulk_lock_pagination_upper_limit`.
// `upper_limit` is the page size used by the Bulk Lock drawer. Defaults to
// 100 when the config is absent. Field uses snake_case to match the
// persisted config payload. Not a feature flag — a tenant-level
// application configuration.
export interface BulkLockPaginationUpperLimitConfig {
  upper_limit: number;
}

// Bulk task lock DTOs -- mirror Dispatch.Platform.Models.Dispatch.BulkTaskLockResponse
// and Dispatch.Platform.Models.Dispatch.BulkTaskOperationOutcome on the server.
// Keep field names and casing aligned with the [JsonProperty(...)] camelCase wire format.
export interface BulkTaskOperationOutcome {
  taskId?: string;
  reason?: string;
}

export interface BulkTaskLockResponse {
  lockedCount: number;
  skippedCount: number;
  failedCount: number;
  lockedTaskIds?: string[];
  skipped?: BulkTaskOperationOutcome[];
  failed?: BulkTaskOperationOutcome[];
  bulkOperationId: string;
}
